import React from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{error?: Error;resetError: () => void;}>;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} resetError={this.resetError} data-id="fg83i6g1f" data-path="src/components/ErrorBoundary.tsx" />;
      }

      return (
        <div className="p-6 space-y-4" data-id="7pz2jtodx" data-path="src/components/ErrorBoundary.tsx">
          <Alert variant="destructive" data-id="2gqys9uhc" data-path="src/components/ErrorBoundary.tsx">
            <AlertTriangle className="h-4 w-4" data-id="qhoe4zskn" data-path="src/components/ErrorBoundary.tsx" />
            <AlertDescription data-id="off39su2g" data-path="src/components/ErrorBoundary.tsx">
              Something went wrong while loading this component.
            </AlertDescription>
          </Alert>
          <div className="text-sm text-gray-600" data-id="l31a7gmcr" data-path="src/components/ErrorBoundary.tsx">
            <p data-id="7s7qeqy9p" data-path="src/components/ErrorBoundary.tsx"><strong data-id="fxbnlx4r8" data-path="src/components/ErrorBoundary.tsx">Error:</strong> {this.state.error?.message}</p>
          </div>
          <Button onClick={this.resetError} variant="outline" data-id="r2p4ukv1g" data-path="src/components/ErrorBoundary.tsx">
            Try Again
          </Button>
        </div>);

    }

    return this.props.children;
  }
}

export default ErrorBoundary;